/**
 * Review Platform Configuration
 *
 * Phase 2A: values now derive from the single source of truth in
 * src/constants/googleReviews.ts so numbers never drift between the
 * Hero badges, the unified GoogleReviewsCarousel, and AggregateRating JSON-LD.
 *
 * SAFE WORDING RULES:
 * - If count < 25: Hide count, use "Highly rated on [Platform]"
 * - If count >= 25: Show count with rating
 * - Never show "0 reviews" or undefined values
 */

import { GOOGLE_REVIEW_STATS, CUSTOMER_METRICS } from './googleReviews';

export interface ReviewSource {
  platform: 'google' | 'housecallpro';
  displayName: string;
  rating: number | null; // null = unknown, hide rating
  reviewCount: number | null; // null = unknown, hide count
  url: string;
  // Featured quotes (optional)
  featuredQuotes?: Array<{
    text: string;
    author?: string;
  }>;
}

export const REVIEW_SOURCES: ReviewSource[] = [
  {
    platform: 'google',
    displayName: 'Google Reviews',
    rating: GOOGLE_REVIEW_STATS.ratingValue,
    reviewCount: GOOGLE_REVIEW_STATS.totalReviews,
    url: GOOGLE_REVIEW_STATS.reviewsUrl,
    featuredQuotes: [
      {
        text: "Professional and reliable. Showed up on time and did excellent work.",
        author: "Local Customer"
      }
    ]
  },
  {
    platform: 'housecallpro',
    displayName: 'Housecall Pro',
    rating: 5.0,
    reviewCount: CUSTOMER_METRICS.totalCustomers,
    url: CUSTOMER_METRICS.housecallProUrl,
    featuredQuotes: [
      {
        text: "Excellent service from start to finish. Highly recommend!",
        author: "Verified Customer"
      },
      {
        text: "Very professional and knowledgeable. Fair pricing too.",
        author: "Verified Customer"
      }
    ]
  }
];

// Threshold for "low count" treatment
export const LOW_COUNT_THRESHOLD = 25;

/**
 * Get safe display text for a review source
 */
export function getReviewDisplayText(source: ReviewSource): {
  primaryText: string;
  secondaryText: string | null;
  showCount: boolean;
} {
  const hasRating = source.rating !== null && source.rating > 0;
  const hasCount = source.reviewCount !== null && source.reviewCount > 0;
  const isLowCount = hasCount && source.reviewCount! < LOW_COUNT_THRESHOLD;

  // Google with low count - emphasize quality, not quantity
  if (source.platform === 'google' && isLowCount) {
    if (hasRating && source.rating! >= 4.5) {
      return {
        primaryText: `${source.rating!.toFixed(1)} Stars`,
        secondaryText: 'Highly rated on Google',
        showCount: false
      };
    }
    return {
      primaryText: 'Trusted by Local Customers',
      secondaryText: 'Customer reviews on Google',
      showCount: false
    };
  }

  // Normal display with count
  if (hasRating && hasCount) {
    return {
      primaryText: `${source.rating!.toFixed(1)} Average Rating`,
      secondaryText: `${source.reviewCount!.toLocaleString()}+ Reviews`,
      showCount: true
    };
  }

  // Rating only
  if (hasRating) {
    return {
      primaryText: `${source.rating!.toFixed(1)} Star Rating`,
      secondaryText: 'Verified Reviews',
      showCount: false
    };
  }

  // Fallback - no specific numbers
  return {
    primaryText: 'Trusted Reviews',
    secondaryText: null,
    showCount: false
  };
}
